<template>
  <section
    class="overflow-hidden select-none"
    :style="`width: ${currentSize}px; height: ${currentSize}px;`"
  >
    <svg
      aria-hidden="true"
      class="origin-top-left"
      :height="SIZES.DEFAULT"
      preserveAspectRatio="xMidYMid meet"
      :style="
        size === 'small'
          ? `transform: scale(${SIZES.SMALL / SIZES.DEFAULT});`
          : undefined
      "
      :viewBox="`0 0 ${SIZES.DEFAULT} ${SIZES.DEFAULT}`"
      :width="SIZES.DEFAULT"
    >
      <defs>
        <path
          :id="`base-shape-path-${id}`"
          d="M36.3127 12.986C39.9599 1.57544 54.103 -2.2142 62.9668 5.84412C65.31 7.97434 68.8109 8.16508 71.3552 6.30113C80.9797 -0.749906 94.6649 4.55588 97.1463 16.3004C97.8022 19.4051 100.53 21.6293 103.683 21.6298C115.609 21.6316 123.609 34.0062 118.76 44.9509C117.478 47.8441 118.391 51.2523 120.948 53.1171C130.619 60.171 129.878 74.8877 119.551 80.8521C116.821 82.4288 115.571 85.7191 116.555 88.7358C120.279 100.148 111.08 111.585 99.2192 110.291C96.0838 109.949 93.1473 111.865 92.1831 114.881C88.536 126.292 74.3929 130.081 65.5291 122.023C63.1859 119.893 59.685 119.702 57.1407 121.566C47.5162 128.617 33.831 123.311 31.3496 111.567C30.6937 108.462 27.9655 106.238 24.813 106.237C12.8873 106.235 4.88733 93.8608 9.73621 82.9161C11.018 80.0229 10.1048 76.6146 7.54809 74.7499C-2.12351 67.696 -1.38258 52.9793 8.94443 47.0149C11.6744 45.4382 12.9249 42.1479 11.9406 39.1312C8.21728 27.7194 17.4161 16.2819 29.2767 17.576C32.4121 17.9181 35.3486 16.0025 36.3127 12.986Z"
        />
        <clipPath :id="`clip-bg-${id}`" clipPathUnits="userSpaceOnUse">
          <use :href="`#base-shape-path-${id}`" />
        </clipPath>
        <!-- Fallback gradient for Safari -->
        <radialGradient
          :id="`fallback-gradient-${id}`"
          cx="0"
          cy="0"
          gradientTransform="translate(97 -54.5) rotate(103.473) scale(246.792)"
          gradientUnits="userSpaceOnUse"
          r="1"
        >
          <stop offset="0%" stop-color="#F4DEEF" />
          <stop offset="20%" stop-color="#E8D4F0" />
          <stop offset="35%" stop-color="#D9E7F7" />
          <stop offset="50%" stop-color="#E6F3F5" />
          <stop offset="65%" stop-color="#F8E6DC" />
          <stop offset="80%" stop-color="#D1F3ED" />
          <stop offset="100%" stop-color="#F5E8DC" />
        </radialGradient>
      </defs>

      <!-- Safari fallback: simple gradient fill -->

      <use
        v-if="isSafari"
        :fill="`url(#fallback-gradient-${id})`"
        :href="`#base-shape-path-${id}`"
      />

      <!-- Non-Safari: foreignObject with parallax background -->
      <g v-else :clip-path="`url(#clip-bg-${id})`">
        <foreignObject
          :height="SIZES.DEFAULT"
          :width="SIZES.DEFAULT"
          x="0"
          y="0"
        >
          <div
            class="bg-fixed bg-center bg-repeat [background-image:var(--bgImg)] [background-size:1536px_1024px] motion-reduce:[background-attachment:unset] md:[background-position:0_-100px]"
            :style="{
              width: `${SIZES.DEFAULT}px`,
              height: `${SIZES.DEFAULT}px`,
              '--bgImg': `url(${imagePath}), radial-gradient(circle at 50% 50%, #F4DEEF 0%, #E8D4F0 20%, #D9E7F7 35%, #E6F3F5 50%, #F8E6DC 65%, #D1F3ED 80%, #F5E8DC 100%)`,
            }"
          />
        </foreignObject>
      </g>

      <g>
        <foreignObject
          class="relative top-0 left-0"
          height="100%"
          width="100%"
          x="0"
          y="0"
        >
          <div
            class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
          >
            <slot name="icon" />
          </div>
        </foreignObject>
        <g>
          <path
            :id="`top-text-path-${topTextId}`"
            :d="
              // reversed path for Firefox
              isFirefox
                ? 'M98.4707,64A34.4707,34.4707 0 0 029.5293,64'
                : 'M29.5293,64a34.4707,34.4707 0 0,1 68.9414,0'
            "
            fill="none"
            transform="rotate(-15 64 64)"
          ></path>
          <text
            :key="`top-text-${id}-${isFirefox}`"
            :class="textClasses"
            fill="currentColor"
            v-bind="{
              ...(isTopTextTooLong && { lengthAdjust: 'spacing' }),
            }"
            :textLength="textLength.topText"
          >
            <textPath
              :href="`#top-text-path-${topTextId}`"
              :side="isFirefox ? 'right' : 'left'"
              startOffset="50%"
              text-anchor="middle"
              :textLength="textLength.topText"
            >
              {{ topText }}
            </textPath>
          </text>
        </g>
        <g>
          <path
            :id="`bottom-text-path-${bottomTextId}`"
            d="M20.58 75.63a44.95 44.95 0 1 0 86.84-23.26 44.95 44.95 0 1 0-86.84 23.26"
            fill="none"
          ></path>
          <text
            :class="[
              textClasses,
              {
                'tracking-wider': isSafari,
              },
            ]"
            fill="currentColor"
            v-bind="{
              ...(isBottomTextTooLong && { lengthAdjust: 'spacing' }),
            }"
            text-anchor="middle"
            :textLength="textLength.bottomText"
          >
            <textPath
              :href="`#bottom-text-path-${bottomTextId}`"
              startOffset="71"
            >
              {{ bottomText }}
            </textPath>
          </text>
        </g>
      </g>
    </svg>
  </section>
</template>

<script setup lang="ts">
import { toPublicPath } from '#imports'
import { computed, onMounted, ref, useId } from 'vue'

import { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { isBrowser } from '@backmarket/utils/env/isBrowser'
import { tw } from '@ds/utils/tailwind'

import translations from './Sticker.translations'

const props = withDefaults(
  defineProps<{
    size?: 'default' | 'small'
  }>(),
  {
    size: 'default',
  },
)

const SIZES = {
  SMALL: 30,
  DEFAULT: 128,
} as const

const id = useId()
const topTextId = useId()
const bottomTextId = useId()

const i18n = useI18n()
const { market } = useMarketplace()
const experiments = useExperiments()

const currentSize = computed(() => {
  return props.size === 'small' ? SIZES.SMALL : SIZES.DEFAULT
})

const isSafari = computed(() => {
  return (
    isBrowser() &&
    navigator.userAgent.includes('Safari') &&
    !navigator.userAgent.includes('Chrome')
  )
})

const isFirefox = computed(() => {
  return isBrowser() && navigator.userAgent.includes('Firefox')
})

const imagePath = toPublicPath('/img/product/verified-refurbished/holo.avif')

// Adding special classes for some copies that are problematic in some countries
const isFR = market.countryCode === MarketCountryCode.FR
const isPT = market.countryCode === MarketCountryCode.PT

const isStickerFlagEnabled = computed(() => {
  return experiments['experiment.ppStickerFlagV3'] === 'withFlag'
})
const isUSMarket = computed(() => {
  return market.countryCode === MarketCountryCode.US
})

const topText = computed(() => {
  if (isUSMarket.value && isStickerFlagEnabled.value) {
    return i18n(translations.topTextLocal)
  }

  return i18n(translations.topText)
})
const bottomText = computed(() => {
  if (isUSMarket.value && isStickerFlagEnabled.value) {
    return i18n(translations.bottomTextLocal)
  }

  return i18n(translations.bottomText)
})

// Translation request says that the maximum length should be 16, but we're
// getting 17 in some cases.
const isTopTextLong = computed(() => topText.value?.length > 13)
const isTopTextTooLong = computed(() => topText.value?.length > 16)

const isBottomTextLong = computed(() => bottomText.value?.length > 13)
const isBottomTextTooLong = computed(() => bottomText.value?.length > 16)

const isTextLong = computed(() => isTopTextLong.value || isBottomTextLong.value)
const isTextTooLong = computed(
  () => isTopTextTooLong.value || isBottomTextTooLong.value,
)

// TODO: Solve by modPDP with props
const textClasses = computed(() =>
  // eslint-disable-next-line no-nested-ternary
  isTextTooLong.value
    ? tw`caption-bold`
    : isTextLong.value
      ? tw`body-2-bold`
      : tw`body-1-bold`,
)

// TODO: Solve by modPDP with props
const computedBottomTextLength = computed(() => {
  if (isPT) {
    return SIZES.DEFAULT - 45
  }

  if (isFR || (isUSMarket.value && isStickerFlagEnabled.value)) {
    return SIZES.DEFAULT - 40
  }

  return SIZES.DEFAULT - 20
})

// TODO: Solve by modPDP with props
const computedTopTextLength = computed(() => {
  return SIZES.DEFAULT
})

const textLength = ref({
  topText: computedTopTextLength.value - 19,
  bottomText: computedBottomTextLength.value - 1,
})
onMounted(() => {
  textLength.value = {
    topText: computedTopTextLength.value - (isSafari.value ? 1.5 : 1) * 19,
    bottomText: computedBottomTextLength.value - (isSafari.value ? 0.8 : 1),
  }
})
</script>
